import React, { useState, useCallback } from 'react';
import { Upload, Download, Check, FileSpreadsheet } from 'lucide-react';
import {
  parseAndConvertCsv,
  toCSV,
  OUTPUT_HEADERS,
  PREVIEW_ROW_COUNT,
  TRUNCATE_CELL_LENGTH,
} from '../utils/cventToInterspire';

export default function AlumniImport() {
  const [convertedRows, setConvertedRows] = useState<string[][] | null>(null);
  const [filename, setFilename] = useState<string | null>(null);
  const [missingHeaders, setMissingHeaders] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const processFile = useCallback((file: File | null) => {
    setError(null);
    setConvertedRows(null);
    setFilename(null);
    setMissingHeaders([]);
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError(`Please drop a CSV file. Received: ${file.name}`);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = reader.result;
        if (typeof text !== 'string') {
          setError('Could not read file as text.');
          return;
        }
        const { rows, missingHeaders: missing } = parseAndConvertCsv(text);
        if (rows.length === 0) {
          setError('The file appears to be empty or had no valid data rows.');
          return;
        }
        setConvertedRows(rows);
        setFilename(file.name);
        setMissingHeaders(missing);
      } catch (err) {
        setError('Error processing file: ' + (err instanceof Error ? err.message : String(err)));
      }
    };
    reader.onerror = () => setError('Failed to read the file.');
    reader.readAsText(file, 'UTF-8');
  }, []);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    processFile(file ?? null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    processFile(file ?? null);
    e.target.value = '';
  };

  const uploadAnother = () => {
    setConvertedRows(null);
    setFilename(null);
    setMissingHeaders([]);
    setError(null);
  };

  const downloadCsv = () => {
    if (!convertedRows || convertedRows.length === 0) return;
    const csv = toCSV(convertedRows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'alumni-interspire-export.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const showDropZone = !convertedRows;
  const displayCount = convertedRows ? Math.min(convertedRows.length, PREVIEW_ROW_COUNT) : 0;

  return (
    <div className="max-w-4xl mx-auto">
      <header className="text-center mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-gray-100 text-gray-600 text-sm font-medium mb-4">
          <FileSpreadsheet className="h-4 w-4" aria-hidden />
          CSV Conversion Tool
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Alumni Export Converter</h1>
        <p className="text-gray-600 max-w-lg mx-auto">
          Drop your Cvent Alumni Export CSV and download the reformatted file for Interspire.
        </p>
      </header>

      {showDropZone && (
        <div
          role="button"
          tabIndex={0}
          onClick={() => document.getElementById('alumni-file-input')?.click()}
          onKeyDown={(e) => e.key === 'Enter' && (document.getElementById('alumni-file-input') as HTMLInputElement)?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`border-2 border-dashed rounded-lg bg-white p-12 text-center cursor-pointer transition-colors ${
            dragOver
              ? 'border-blue-500 bg-blue-50/50'
              : 'border-gray-300 hover:border-blue-500 hover:bg-gray-50/50'
          }`}
        >
          <input
            id="alumni-file-input"
            type="file"
            accept=".csv"
            className="hidden"
            onChange={handleFileInputChange}
          />
          <Upload className="h-12 w-12 mx-auto mb-4 text-gray-400" aria-hidden />
          <p className="font-semibold text-gray-900">Drag & drop your CSV here</p>
          <p className="text-sm text-gray-500 mt-1">or click to browse</p>
        </div>
      )}

      {error && (
        <div role="alert" className="mt-4 p-4 rounded-lg bg-red-50 text-red-700 border border-red-200 text-sm">
          {error}
        </div>
      )}

      {missingHeaders.length > 0 && convertedRows && (
        <div className="mt-4 p-4 rounded-lg bg-amber-50 text-amber-800 border border-amber-200 text-sm">
          Warning: Some expected columns are missing: {missingHeaders.join(', ')}. Conversion may be incomplete.
        </div>
      )}

      {convertedRows && filename && (
        <>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-4 p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="flex flex-col gap-1">
              <span className="inline-flex items-center gap-2 text-green-700 font-medium">
                <Check className="h-5 w-5" />
                {convertedRows.length} rows converted
              </span>
              <span className="text-sm text-gray-500">{filename}</span>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={uploadAnother}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Upload another
              </button>
              <button
                type="button"
                onClick={downloadCsv}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                <Download className="h-4 w-4" />
                Download CSV
              </button>
            </div>
          </div>

          <section className="mt-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
              Preview — first {displayCount} of {convertedRows.length} rows
            </h2>
            <div className="overflow-x-auto border border-gray-200 rounded-lg bg-white shadow-sm">
              <table className="min-w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    {OUTPUT_HEADERS.map((h) => (
                      <th key={h} className="px-3 py-2.5 text-left font-semibold text-gray-900 border-b border-gray-200 whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {convertedRows.slice(0, PREVIEW_ROW_COUNT).map((row, rowIdx) => (
                    <tr key={rowIdx} className="hover:bg-gray-50/50 border-b border-gray-100 last:border-b-0">
                      {row.map((cell, colIdx) => {
                        const val = cell || '';
                        const truncate = val.length > TRUNCATE_CELL_LENGTH;
                        return (
                          <td
                            key={colIdx}
                            className={`px-3 py-2 text-gray-700 max-w-[180px] ${truncate ? 'truncate' : ''}`}
                            title={truncate ? val : undefined}
                          >
                            {val}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
